import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Crown, Dog, Cat, Bird, Fish, Rabbit,
  PawPrint, Palette, Building2, CheckCircle, Loader2, Sparkles,
  Heart, Users, Calendar, ShoppingBag, Camera, BookOpen, Trophy,
  MapPin, Globe, AlertCircle, HelpCircle, Image, FileText, 
  Scale, Mail, ExternalLink, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Pet type options
const PET_TYPES = [
  { id: 'dog', label: 'Dogs', icon: Dog, description: 'Canine companions' },
  { id: 'cat', label: 'Cats', icon: Cat, description: 'Feline friends' },
  { id: 'bird', label: 'Birds', icon: Bird, description: 'Feathered pets' },
  { id: 'fish', label: 'Fish', icon: Fish, description: 'Aquatic pets' },
  { id: 'rabbit', label: 'Rabbits', icon: Rabbit, description: 'Small mammals' },
  { id: 'reptile', label: 'Reptiles', icon: PawPrint, description: 'Scaled companions' },
  { id: 'rodent', label: 'Rodents', icon: PawPrint, description: 'Hamsters, gerbils, guinea pigs' },
  { id: 'horse', label: 'Horses', icon: PawPrint, description: 'Equine animals' },
  { id: 'exotic', label: 'Exotic Pets', icon: Sparkles, description: 'Unusual companions' },
  { id: 'multi', label: 'Multiple Types', icon: Heart, description: 'All pets welcome' },
  { id: 'other', label: 'Other', icon: HelpCircle, description: 'Specify your pet type' },
];

// Countries list (common ones)
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'IN', name: 'India' },
  { code: 'OTHER', name: 'Other' },
];

// Languages list (ISO 639-1 codes)
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ru', name: 'Russian' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'other', name: 'Other' },
];

// US States
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Club type options
const CLUB_TYPES = [
  { id: 'standard', label: 'Standard Club', description: 'Community-based pet club', fee: '$99 setup' },
  { id: 'ngo', label: 'Non-Profit / Rescue', description: 'Animal welfare organization', fee: 'Free' },
  { id: 'breeder', label: 'Breeder Network', description: 'Professional breeding community', fee: '$199 setup' },
];

// Color presets
const COLOR_PRESETS = [
  { name: 'Classic Brown', primary: '#c9762b', secondary: '#a65d1f', accent: '#e3c992', description: 'Warm & traditional' },
  { name: 'Forest Green', primary: '#166534', secondary: '#14532d', accent: '#86efac', description: 'Natural & fresh' },
  { name: 'Ocean Blue', primary: '#1e40af', secondary: '#1e3a8a', accent: '#93c5fd', description: 'Cool & professional' },
  { name: 'Royal Purple', primary: '#7c3aed', secondary: '#6d28d9', accent: '#c4b5fd', description: 'Bold & modern' },
  { name: 'Sunset Orange', primary: '#ea580c', secondary: '#c2410c', accent: '#fed7aa', description: 'Energetic & warm' },
  { name: 'Rose Pink', primary: '#db2777', secondary: '#be185d', accent: '#fbcfe8', description: 'Friendly & fun' },
  { name: 'Slate Gray', primary: '#475569', secondary: '#334155', accent: '#cbd5e1', description: 'Professional & neutral' },
  { name: 'Teal', primary: '#0d9488', secondary: '#0f766e', accent: '#99f6e4', description: 'Fresh & calming' },
];

// Feature options for initial setup
const INITIAL_FEATURES = [
  { id: 'events', label: 'Events & Calendar', icon: Calendar, default: true },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag, default: true },
  { id: 'photo_albums', label: 'Photo Albums', icon: Camera, default: true },
  { id: 'community_forums', label: 'Community Forums', icon: Users, default: true },
  { id: 'health_records', label: 'Health Records', icon: Heart, default: false },
  { id: 'breeding_tools', label: 'Breeding Tools', icon: PawPrint, default: false },
  { id: 'competitions', label: 'Competitions & Shows', icon: Trophy, default: false },
  { id: 'resources_library', label: 'Resources Library', icon: BookOpen, default: true },
];

// Why create club options
const WHY_CREATE_OPTIONS = [
  { id: 'connect_owners', label: 'Connect pet owners in my area', description: 'Build a local community' },
  { id: 'organize_events', label: 'Organize events & meetups', description: 'Dog shows, playdates, training sessions' },
  { id: 'breed_preservation', label: 'Preserve & promote a specific breed', description: 'Breed education & standards' },
  { id: 'rescue_support', label: 'Support rescue & adoption efforts', description: 'Help animals find homes' },
  { id: 'professional_network', label: 'Network with breeders & professionals', description: 'Business & breeding connections' },
  { id: 'education', label: 'Share educational resources', description: 'Training, health, care information' },
  { id: 'competitions', label: 'Organize competitions & shows', description: 'Conformation, agility, obedience' },
  { id: 'hobby', label: 'Share a hobby or special interest', description: 'Hiking with dogs, pet photography, etc.' },
  { id: 'other', label: 'Other reason', description: 'Tell us more below' },
];

export default function CreateClub() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [existingClubs, setExistingClubs] = useState([]);
  const [showExistingClubs, setShowExistingClubs] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Basics
    name: '',
    slug: '',
    tagline: '',
    motto: '',  // Club motto
    description: '',
    mission: '',
    purpose: '',  // Purpose statement
    
    // Step 2: Pet Types & Club Type
    pet_types: [],
    other_pet_type: '',  // For "other" pet type
    club_type: 'standard',
    
    // Step 3: Location/Region
    country: 'US',
    state: '',
    region: '',  // Optional region name
    default_language: 'en',  // Preferred language
    
    // Step 4: Branding
    primary_color: '#c9762b',
    secondary_color: '#a65d1f',
    accent_color: '#e3c992',
    selectedPreset: 'Classic Brown',
    logo_url: '',
    banner_url: '',
    
    // Step 5: Features
    features: INITIAL_FEATURES.filter(f => f.default).map(f => f.id),
    
    // Step 6: Why Create
    why_create: [],
    why_create_other: '',
    
    // Step 7: Contact & Agreements
    website_url: '',
    email: '',
    allows_free_members: true,
    bylaws_accepted: false,
    service_agreement_accepted: false,
    request_modification: false,
    modification_notes: '',
  });

  const isPremium = user?.membership_tier === 'premium';
  const totalSteps = 7;  // Now 7 steps (added Why step)

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const togglePetType = (petId) => {
    setFormData(prev => ({
      ...prev,
      pet_types: prev.pet_types.includes(petId)
        ? prev.pet_types.filter(p => p !== petId)
        : [...prev.pet_types, petId]
    }));
  };

  // Check for existing clubs with similar pet types in same region
  const checkExistingClubs = useCallback(async () => {
    if (formData.pet_types.length === 0 || !formData.country) return;
    
    setCheckingExisting(true);
    try {
      // Build pet types list including custom "other" type
      const petTypes = formData.pet_types.includes('other') && formData.other_pet_type
        ? [...formData.pet_types.filter(p => p !== 'other'), formData.other_pet_type.toLowerCase()]
        : formData.pet_types;
      
      const response = await axios.get(`${API}/api/clubs/search/similar`, {
        params: {
          pet_types: petTypes.join(','),
          country: formData.country,
          state: formData.state || undefined,
        }
      });
      
      const clubs = response.data.clubs || [];
      setExistingClubs(clubs);
      
      if (clubs.length > 0) {
        setShowExistingClubs(true);
      }
    } catch (error) {
      console.error('Error checking existing clubs:', error);
      // Continue anyway if check fails
    } finally {
      setCheckingExisting(false);
    }
  }, [formData.pet_types, formData.other_pet_type, formData.country, formData.state]);

  // Check for existing clubs when moving from step 3 (location) to step 4
  useEffect(() => {
    if (step === 3 && formData.pet_types.length > 0 && formData.country) {
      checkExistingClubs();
    }
  }, [step, checkExistingClubs]);

  const selectColorPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
      selectedPreset: preset.name
    }));
  };

  const toggleFeature = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const toggleWhyCreate = (reasonId) => {
    setFormData(prev => ({
      ...prev,
      why_create: prev.why_create.includes(reasonId)
        ? prev.why_create.filter(r => r !== reasonId)
        : [...prev.why_create, reasonId]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.slug && formData.tagline && formData.description;
      case 2:
        // Must have pet types, and if "other" is selected, must have custom type
        const hasValidPetTypes = formData.pet_types.length > 0;
        const hasOtherType = formData.pet_types.includes('other') ? formData.other_pet_type.trim() : true;
        return hasValidPetTypes && hasOtherType;
      case 3:
        return formData.country; // Location is required
      case 4:
        return formData.primary_color; // Branding
      case 5:
        return true; // Features are optional
      case 6:
        // Why create - at least one reason required, and if "other" selected, must have text
        const hasReason = formData.why_create.length > 0;
        const hasOtherReason = formData.why_create.includes('other') ? formData.why_create_other.trim() : true;
        return hasReason && hasOtherReason;
      case 7:
        // Must accept both agreements
        return formData.bylaws_accepted && formData.service_agreement_accepted;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!isPremium) {
      toast.error('Premium membership required to create a club');
      return;
    }

    if (!formData.bylaws_accepted || !formData.service_agreement_accepted) {
      toast.error('You must accept the Club Bylaws and Service Agreement');
      return;
    }

    setLoading(true);
    try {
      // Build feature config
      const feature_config = {};
      INITIAL_FEATURES.forEach(f => {
        feature_config[f.id] = formData.features.includes(f.id);
      });

      // Build pet types list including custom "other" type
      let petTypes = formData.pet_types.filter(p => p !== 'other');
      if (formData.pet_types.includes('other') && formData.other_pet_type) {
        petTypes.push(formData.other_pet_type.toLowerCase());
      }

      const now = new Date().toISOString();

      const response = await axios.post(`${API}/api/clubs/create`, {
        name: formData.name,
        slug: formData.slug,
        tagline: formData.tagline,
        motto: formData.motto || null,
        description: formData.description,
        mission: formData.mission || `Building a community for ${petTypes.join(', ')} lovers.`,
        purpose: formData.purpose || null,
        pet_types: petTypes,
        club_type: formData.club_type,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        accent_color: formData.accent_color,
        logo_url: formData.logo_url || null,
        banner_url: formData.banner_url || null,
        website_url: formData.website_url || null,
        email: formData.email || null,
        country: formData.country,
        state: formData.state || null,
        region: formData.region || null,
        default_language: formData.default_language,
        available_countries: [formData.country],
        allows_free_members: formData.allows_free_members,
        feature_config: feature_config,
        // Why create fields
        why_create: formData.why_create,
        why_create_other: formData.why_create.includes('other') ? formData.why_create_other : null,
        // Agreement fields
        bylaws_accepted: formData.bylaws_accepted,
        bylaws_accepted_at: now,
        service_agreement_accepted: formData.service_agreement_accepted,
        service_agreement_accepted_at: now,
        agreement_modification_requested: formData.request_modification,
        agreement_modification_notes: formData.request_modification ? formData.modification_notes : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Club created successfully!');
      navigate(`/creator-dashboard`);
    } catch (error) {
      console.error('Error creating club:', error);
      toast.error(error.response?.data?.detail || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  // Premium required check
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="create-club-upgrade">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Crown className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-3xl font-heading font-medium mb-4">Premium Feature</h1>
          <p className="text-muted-foreground mb-8">
            Club creation is an exclusive Premium feature. Upgrade to Premium to create 
            and manage your own pet community clubs.
          </p>
          <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
            <Link to="/membership/upgrade">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="create-club-page">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/creator-dashboard" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Creator Dashboard
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-heading font-medium">Create Your Club</h1>
          </div>
          <p className="text-muted-foreground">
            Set up your new pet community in a few simple steps.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span className="text-muted-foreground">
              {step === 1 && 'Basic Info'}
              {step === 2 && 'Pet Types'}
              {step === 3 && 'Location'}
              {step === 4 && 'Branding'}
              {step === 5 && 'Features'}
              {step === 6 && 'Why Create'}
              {step === 7 && 'Agreements'}
            </span>
          </div>
          <Progress value={(step / totalSteps) * 100} className="h-2" />
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Club Information</CardTitle>
              <CardDescription>Tell us about your new club</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Club Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="e.g., Golden Retriever Lovers Club"
                  data-testid="club-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="slug">URL Slug *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">allamericandog.club/</span>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                    placeholder="golden-retriever-lovers"
                    className="flex-1"
                    data-testid="club-slug-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="tagline">Tagline *</Label>
                <Input
                  id="tagline"
                  value={formData.tagline}
                  onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="A short, catchy description"
                  data-testid="club-tagline-input"
                />
              </div>
              
              <div>
                <Label htmlFor="motto">Club Motto</Label>
                <Input
                  id="motto"
                  value={formData.motto}
                  onChange={(e) => setFormData(prev => ({ ...prev, motto: e.target.value }))}
                  placeholder="e.g., 'Where Every Pup is a Champion'"
                  data-testid="club-motto-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Tell potential members what your club is about, what activities you offer, who should join..."
                  rows={3}
                  data-testid="club-description-input"
                />
              </div>
              
              <div>
                <Label htmlFor="purpose">Purpose Statement</Label>
                <Textarea
                  id="purpose"
                  value={formData.purpose}
                  onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                  placeholder="Why does this club exist? What problem does it solve or need does it meet?"
                  rows={2}
                  data-testid="club-purpose-input"
                />
              </div>
              
              <div>
                <Label htmlFor="mission">Mission Statement</Label>
                <Textarea
                  id="mission"
                  value={formData.mission}
                  onChange={(e) => setFormData(prev => ({ ...prev, mission: e.target.value }))}
                  placeholder="What are your club's long-term goals?"
                  rows={2}
                  data-testid="club-mission-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Pet Types & Club Type */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Pet Types</CardTitle>
                <CardDescription>What pets will your club focus on? Select all that apply.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {PET_TYPES.map(pet => {
                    const Icon = pet.icon;
                    const isSelected = formData.pet_types.includes(pet.id);
                    return (
                      <button
                        key={pet.id}
                        type="button"
                        onClick={() => togglePetType(pet.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border text-center transition-all ${
                          isSelected 
                            ? 'bg-primary/10 border-primary ring-2 ring-primary/20' 
                            : 'bg-background hover:bg-muted border-border'
                        }`}
                        data-testid={`pet-type-${pet.id}`}
                      >
                        <Icon className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="font-medium text-sm">{pet.label}</span>
                        <span className="text-xs text-muted-foreground">{pet.description}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Other pet type fill-in */}
                {formData.pet_types.includes('other') && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <Label htmlFor="other-pet-type">Specify your pet type *</Label>
                    <Input
                      id="other-pet-type"
                      value={formData.other_pet_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, other_pet_type: e.target.value }))}
                      placeholder="e.g., Ferrets, Sugar Gliders, Alpacas..."
                      className="mt-2"
                      data-testid="other-pet-type-input"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Club Type</CardTitle>
                <CardDescription>What type of organization is this?</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.club_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, club_type: value }))}
                  className="space-y-3"
                >
                  {CLUB_TYPES.map(type => (
                    <div 
                      key={type.id}
                      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.club_type === type.id ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, club_type: type.id }))}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={type.id} id={type.id} />
                        <div>
                          <Label htmlFor={type.id} className="font-medium cursor-pointer">{type.label}</Label>
                          <p className="text-sm text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                      <Badge variant={type.fee === 'Free' ? 'secondary' : 'outline'}>{type.fee}</Badge>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Location / Region */}
        {step === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Club Location
                </CardTitle>
                <CardDescription>Where is your club based? This helps members find local communities.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value, state: '' }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="country-select"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                
                {formData.country === 'US' && (
                  <div>
                    <Label htmlFor="state">State</Label>
                    <select
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="state-select"
                    >
                      <option value="">Select a state (optional)</option>
                      {US_STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="region">Region / Area Name</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                    placeholder="e.g., Pacific Northwest, Southern California, Greater London..."
                    data-testid="region-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Optional - helps describe your club's geographic focus</p>
                </div>
                
                <div>
                  <Label htmlFor="language">Preferred Language</Label>
                  <select
                    id="language"
                    value={formData.default_language}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_language: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="language-select"
                  >
                    {LANGUAGES.map(l => (
                      <option key={l.code} value={l.code}>{l.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">The primary language for club communications</p>
                </div>
              </CardContent>
            </Card>

            {/* Existing Clubs Alert */}
            {showExistingClubs && existingClubs.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    Similar Clubs Found
                  </CardTitle>
                  <CardDescription>
                    There are already clubs for similar pet types in your region. You might want to join one instead!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {existingClubs.map(club => (
                      <div 
                        key={club.id} 
                        className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      >
                        <div>
                          <div className="font-medium">{club.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {club.pet_types?.join(', ')} • {club.region || club.state || club.country}
                          </div>
                          {club.motto && (
                            <div className="text-xs text-muted-foreground italic">"{club.motto}"</div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline">{club.member_count || 0} members</Badge>
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/clubs/${club.slug}`}>View</Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-amber-700 mt-4">
                    It's okay to create a new club even if similar ones exist - multiple communities can thrive!
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => setShowExistingClubs(false)}
                  >
                    Continue Creating My Club
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {checkingExisting && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking for similar clubs...
              </div>
            )}
          </div>
        )}

        {/* Step 4: Branding */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Club Branding
              </CardTitle>
              <CardDescription>Choose your club's color scheme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color Presets */}
              <div>
                <Label className="mb-3 block">Color Presets</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {COLOR_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => selectColorPreset(preset)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.selectedPreset === preset.name 
                          ? 'ring-2 ring-primary border-primary' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`color-preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex gap-1 mb-2">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.primary }} />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.secondary }} />
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: preset.accent }} />
                      </div>
                      <div className="text-sm font-medium">{preset.name}</div>
                      <div className="text-xs text-muted-foreground">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div>
                <Label className="mb-3 block">Or Customize</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="primary" className="text-xs">Primary</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="primary"
                        value={formData.primary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondary" className="text-xs">Secondary</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="secondary"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="accent" className="text-xs">Accent</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        id="accent"
                        value={formData.accent_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.accent_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, accent_color: e.target.value, selectedPreset: 'Custom' }))}
                        className="flex-1 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label className="mb-3 block">Preview</Label>
                <div 
                  className="rounded-lg p-6 text-white"
                  style={{ backgroundColor: formData.primary_color }}
                >
                  <h3 className="font-heading text-xl mb-2">{formData.name || 'Your Club Name'}</h3>
                  <p className="text-sm opacity-90 mb-4">{formData.tagline || 'Your tagline here'}</p>
                  <div className="flex gap-2">
                    <button 
                      className="px-4 py-2 rounded text-sm font-medium"
                      style={{ backgroundColor: formData.secondary_color }}
                    >
                      Join Club
                    </button>
                    <button 
                      className="px-4 py-2 rounded text-sm font-medium"
                      style={{ backgroundColor: formData.accent_color, color: formData.primary_color }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Features */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle>Club Features</CardTitle>
              <CardDescription>Choose which features to enable for your club. You can change these later.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {INITIAL_FEATURES.map(feature => {
                  const Icon = feature.icon;
                  const isSelected = formData.features.includes(feature.id);
                  return (
                    <div
                      key={feature.id}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleFeature(feature.id)}
                    >
                      <Checkbox 
                        checked={isSelected} 
                        onCheckedChange={() => toggleFeature(feature.id)}
                        data-testid={`feature-${feature.id}`}
                      />
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium">{feature.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Why Create This Club */}
        {step === 6 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Why Are You Creating This Club?
              </CardTitle>
              <CardDescription>
                Select all the reasons that apply. This helps us understand your goals and provide better support.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hint about welcome message */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-800">
                  <strong>Tip:</strong> Your selections here will become your club's draft welcome message — 
                  a pinned post that greets new members and explains why you started this community.
                </p>
              </div>
              
              <div className="space-y-3">
                {WHY_CREATE_OPTIONS.map(option => {
                  const isSelected = formData.why_create.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleWhyCreate(option.id)}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleWhyCreate(option.id)}
                        className="mt-0.5"
                        data-testid={`why-${option.id}`}
                      />
                      <div>
                        <span className="font-medium">{option.label}</span>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Other reason text field */}
              {formData.why_create.includes('other') && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <Label htmlFor="why-other">Tell us more about your reason *</Label>
                  <Textarea
                    id="why-other"
                    value={formData.why_create_other}
                    onChange={(e) => setFormData(prev => ({ ...prev, why_create_other: e.target.value }))}
                    placeholder="Describe why you want to create this club..."
                    rows={3}
                    className="mt-2"
                    data-testid="why-other-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 7: Agreements & Final Settings */}
        {step === 7 && (
          <div className="space-y-6">
            {/* Contact Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Contact & Settings</CardTitle>
                <CardDescription>Optional details to complete your club setup</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="email">Club Contact Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@yourclub.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website URL</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="https://yourclub.com"
                  />
                </div>
                
                <div className="flex items-center gap-3 p-4 rounded-lg border">
                  <Checkbox
                    id="free-members"
                    checked={formData.allows_free_members}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allows_free_members: checked }))}
                  />
                  <div>
                    <Label htmlFor="free-members" className="font-medium cursor-pointer">Allow Free Members</Label>
                    <p className="text-sm text-muted-foreground">Free users can view public content (recommended)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal Agreements */}
            <Card className="border-amber-200">
              <CardHeader className="bg-amber-50/50">
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-amber-700" />
                  Club Agreements
                </CardTitle>
                <CardDescription>
                  Please review and accept the following agreements to create your club
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {/* Club Bylaws Agreement */}
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  formData.bylaws_accepted ? 'border-green-500 bg-green-50/50' : 'border-border'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="bylaws"
                      checked={formData.bylaws_accepted}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bylaws_accepted: checked }))}
                      className="mt-1"
                      data-testid="bylaws-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="bylaws" className="font-medium cursor-pointer flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        I accept the Standard Club Bylaws *
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        The bylaws establish governance rules, membership policies, and operational guidelines for your club.
                      </p>
                      <Link 
                        to="/about/bylaws" 
                        target="_blank"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Read the Club Bylaws
                      </Link>
                    </div>
                  </div>
                </div>

                {/* VBB Service Agreement */}
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  formData.service_agreement_accepted ? 'border-green-500 bg-green-50/50' : 'border-border'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="service-agreement"
                      checked={formData.service_agreement_accepted}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, service_agreement_accepted: checked }))}
                      className="mt-1"
                      data-testid="service-agreement-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="service-agreement" className="font-medium cursor-pointer flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        I accept the VBB Service Agreement *
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        The service agreement covers technology services, revenue sharing (10%), support, and hosting provided by Very Big Box LLC.
                      </p>
                      <Link 
                        to="/about/service-agreement" 
                        target="_blank"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Read the VBB Service Agreement
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Request Modification Option */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="request-modification"
                      checked={formData.request_modification}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, request_modification: checked }))}
                      className="mt-1"
                      data-testid="modification-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="request-modification" className="font-medium cursor-pointer flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Request Agreement Modifications
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        If you need modifications to the standard agreements, you may accept them as written now and submit a request for changes.
                        Our team will contact you to discuss your needs.
                      </p>
                    </div>
                  </div>
                  
                  {formData.request_modification && (
                    <div className="mt-4 ml-6">
                      <Label htmlFor="modification-notes">What modifications would you like to request?</Label>
                      <Textarea
                        id="modification-notes"
                        value={formData.modification_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, modification_notes: e.target.value }))}
                        placeholder="Describe the changes you'd like to request..."
                        rows={3}
                        className="mt-2"
                        data-testid="modification-notes-input"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Your club will be created with the standard agreements. We'll review your modification request and follow up via email.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Review Your Club
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Club Name:</span>
                    <p className="font-medium">{formData.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">URL:</span>
                    <p className="font-medium">/{formData.slug}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pet Types:</span>
                    <p className="font-medium">{formData.pet_types.join(', ') || 'None selected'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Club Type:</span>
                    <p className="font-medium capitalize">{formData.club_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Location:</span>
                    <p className="font-medium">{formData.region || formData.state || formData.country}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Color Theme:</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: formData.primary_color }} />
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: formData.secondary_color }} />
                        <div className="w-4 h-4 rounded border" style={{ backgroundColor: formData.accent_color }} />
                      </div>
                      <span className="font-medium">{formData.selectedPreset}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Features:</span>
                    <p className="font-medium">{formData.features.length} enabled</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Agreements:</span>
                    <p className="font-medium">
                      {formData.bylaws_accepted && formData.service_agreement_accepted 
                        ? '✓ Both accepted' 
                        : 'Pending acceptance'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className="bg-primary"
              data-testid="create-club-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Club
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
